import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

export const FORM_DESCRIPTION_MAX = 500;
export const JOB_OFFER_DESCRIPTION_MAX = 10000;

const InlineToggle: React.FC<{ checked: boolean; onChange: () => void }> = ({ checked, onChange }) => (
  <span
    onClick={(e) => { e.stopPropagation(); onChange(); }}
    style={{
      position: "relative",
      width: "36px",
      height: "20px",
      borderRadius: "10px",
      background: checked ? "#0065DD" : "#cbd5e1",
      transition: "background 0.2s",
      flexShrink: 0,
      display: "inline-block",
      cursor: "pointer",
    }}
  >
    <span
      style={{
        position: "absolute",
        top: "2px",
        left: checked ? "18px" : "2px",
        width: "16px",
        height: "16px",
        borderRadius: "50%",
        background: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        transition: "left 0.2s",
      }}
    />
  </span>
);

interface DescriptionSectionProps {
  title: string;
  hint: string;
  enabled: boolean;
  onToggleEnabled: () => void;
  children: React.ReactNode;
}

export const DescriptionSection: React.FC<DescriptionSectionProps> = ({
  title,
  hint,
  enabled,
  onToggleEnabled,
  children,
}) => {
  // Sticky toolbars inside `children` need to escape to the modal's scroll
  // container. `overflow: hidden` on this section (or on the motion.div) would
  // trap them. We clip only while the expand/collapse animation runs.
  const [clip, setClip] = useState(true);

  return (
    <div
      style={{
        marginBottom: "16px",
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        background: enabled ? "#fff" : "#f8fafc",
      }}
    >
      <div
        onClick={onToggleEnabled}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "12px 14px",
          cursor: "pointer",
          borderTopLeftRadius: "11px",
          borderTopRightRadius: "11px",
        }}
      >
        <InlineToggle checked={enabled} onChange={onToggleEnabled} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "13px",
              fontWeight: 600,
              color: enabled ? "#0050b3" : "#475569",
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "11.5px",
              color: "#64748b",
              marginTop: "2px",
              lineHeight: 1.45,
            }}
          >
            {hint}
          </div>
        </div>
      </div>
      <AnimatePresence initial={false} onExitComplete={() => setClip(true)}>
        {enabled && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            onAnimationStart={() => setClip(true)}
            onAnimationComplete={(def: { opacity?: number }) => {
              if (def?.opacity === 1) setClip(false);
            }}
            style={{ overflow: clip ? "hidden" : "visible" }}
          >
            <div style={{ padding: "0 14px 14px 14px" }}>{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface PlainDescriptionFieldProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

export const PlainDescriptionField: React.FC<PlainDescriptionFieldProps> = ({
  value,
  onChange,
  placeholder = "Describe brevemente el objetivo o propósito de este formulario…",
}) => {
  const length = value.length;
  const over = length > FORM_DESCRIPTION_MAX;
  return (
    <div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, FORM_DESCRIPTION_MAX))}
        placeholder={placeholder}
        rows={3}
        style={{
          width: "100%",
          resize: "vertical",
          minHeight: "72px",
          padding: "10px 12px",
          borderRadius: "8px",
          border: `1px solid ${over ? "#dc2626" : "#e2e8f0"}`,
          background: "#fff",
          fontFamily: "Roboto, sans-serif",
          fontSize: "13px",
          color: "#1e293b",
          outline: "none",
          boxSizing: "border-box",
        }}
      />
      <div
        style={{
          textAlign: "right",
          fontFamily: "Roboto, sans-serif",
          fontSize: "11px",
          color: over ? "#dc2626" : "#94a3b8",
          fontWeight: over ? 600 : 400,
          marginTop: "4px",
        }}
      >
        {length} / {FORM_DESCRIPTION_MAX}
      </div>
    </div>
  );
};
